import IconInfo from '@apollo/icons/default/IconInfo.svg';
import IconSupport from '@apollo/icons/default/IconSupport.svg';
import React from 'react';

import { ClickableText } from 'src/components/common/clickableText/ClickableText';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { usePrivateSubgraphSyncMutation } from 'src/hooks/useCloudResourceManagement';

import { CommonCloudDedicatedProps, CommonStepProps } from '../shared';

import { ARNInputFormControl } from './ArnInputFormControl';

export const OnboardingPrivateResourcesSection = ({
  errors,
  values,
  setFieldValue,
  handleBlur,
  availableUnregisteredResources,
  setAvailableUnregisteredResources,
  sync,
  sharedResources,
  sharedResourcesError,
  sharedResourcesLoading,
}: Pick<CommonStepProps, 'errors' | 'setFieldValue' | 'handleBlur' | 'values'> &
  Pick<
    CommonCloudDedicatedProps,
    'availableUnregisteredResources' | 'setAvailableUnregisteredResources'
  > & {
    sync: ReturnType<typeof usePrivateSubgraphSyncMutation>['sync'];
    sharedResources: ReturnType<typeof usePrivateSubgraphSyncMutation>['data'];
    sharedResourcesLoading: ReturnType<
      typeof usePrivateSubgraphSyncMutation
    >['loading'];
    sharedResourcesError: ReturnType<
      typeof usePrivateSubgraphSyncMutation
    >['error'];
  }) => {
  const arnTooltipText =
    'An Amazon Resource Name (ARN) is an official identifier provided by AWS to uniquely recognize any resource you create, such as a Resource Share. By providing us with this ARN, you enable our platform to securely and efficiently interact with your AWS resources.';

  const rsTooltipText =
    'A Resource Share is a virtual container that holds resources you want to share across different AWS accounts or organizational units. In other words, Resource Shares provide a way to bundle resources you would like to share with other AWS accounts or parts of your organization.';

  return (
    <>
      <div className="mb-6">
        <span className="text-secondary">
          Our platform integrates seamlessly and securely with AWS. To proceed,
          we require the
        </span>
        <Tooltip className="w-72" label={arnTooltipText} placement="start">
          <span
            className="mx-1 size-5"
            data-testid="focusable-info-endpoint-url"
          >
            <span className="font-semibold text-secondary">
              Amazon Resource Name (ARN){' '}
            </span>
            <IconInfo className="text-secondary" />
          </span>
        </Tooltip>{' '}
        <span className="text-secondary"> for the </span>
        <Tooltip className="w-72" label={rsTooltipText} placement="right-start">
          <span
            className="mr-1 size-5"
            data-testid="focusable-info-endpoint-url"
          >
            <span className="font-semibold text-secondary">
              Resource Share{' '}
            </span>
            <IconInfo className="text-secondary" />
          </span>
        </Tooltip>{' '}
        <span className="text-secondary">
          you would like to link to this Router.
        </span>
      </div>

      <div className="text-secondary">
        If you already have an AWS account and a Resource Share ARN, please
        enter your ARN below and click the{' '}
        <span className="text-base font-semibold">Next</span> button.
      </div>
      <ARNInputFormControl
        values={values}
        errors={errors}
        setFieldValue={setFieldValue}
        handleBlur={handleBlur}
        availableUnregisteredResources={availableUnregisteredResources}
        setAvailableUnregisteredResources={setAvailableUnregisteredResources}
        sync={sync}
        sharedResourcesError={sharedResourcesError}
        sharedResources={sharedResources}
        sharedResourcesLoading={sharedResourcesLoading}
      />
      <div className="my-6 h-px bg-silver-dark" />

      <div className="mb-4 flex items-center">
        <IconSupport className="mr-2 text-icon-primary" />
        <span className="mr-2 text-lg font-semibold">
          Need help finding your ARN?
        </span>
        <span className="text-secondary">
          Check out our{' '}
          <ClickableText
            as="a"
            className="text-link"
            target="_blank"
            rel="noopener noreferrer"
            href="https://www.apollographql.com/docs/graphos/routing/cloud-dedicated/lattice"
          >
            guide to adding a private resource.
          </ClickableText>
        </span>
      </div>
    </>
  );
};
